import { createSlice } from "@reduxjs/toolkit";

import type { ViewerFileType } from "@/components/FileViewer/services/fileViewerInterface";
import type { SourceOrigin } from "@/components/FileViewer/sourceOrigin";

export interface FileViewerState {
  /** Invalidates pending lookups when a later viewer intent opens or closes. */
  navigationEpoch: number;
  /** Blob id of the document being viewed. Null for sources that aren't blobs
   *  — the IODL result table, and files a chat turn produced (#10988). */
  fileId: string | null;
  fileType: ViewerFileType | null;
  fileName: string | null;
  /** Why an unreadable file cannot be read (#11277), when the opener knows:
   *  the viewer then names it. Null for every other file. */
  unreadableReason: "encrypted" | "no_signature" | null;
  isOpen: boolean;
  doNotOpenDrawer: boolean;
  sheetName: string | null;
  highlightPageNum: number | null;
  highlightContext: string | null;
  /** Opt-in for whole-document numeric table evidence lookup. */
  citationRowLabel?: string | null;
  originalNumValue: string | number | null;
  parentElementText: string | null;
  // Highlight the cited value where the page prints it, or nothing: no
  // passage fallback (#12090). A table artifact cell sets it.
  exactOnly: boolean;
  highlightCell: string | null;
  // A file produced by a chat turn is addressed by (conversation, turn,
  // filename) rather than by blob id, so the viewer is handed the URLs to
  // render from and to download from. Null for ordinary documents, which the
  // viewer resolves from `fileId`.
  previewUrl: string | null;
  downloadUrl: string | null;
  // Set when the open file is a registered artifact the viewer may write
  // back to (#11523): hand-edits PUT /artifacts/{id}/content. Null for
  // everything else — a viewer with no artifactId never persists edits.
  artifactId: string | null;
  // Explicit interaction context, with or without a backing blob.
  // Rows sharing a blob remain distinct viewer identities.
  interactionId: string | null;
  /** A refused explicit lookup must not display a previously cached row. */
  interactionError: string | null;
  // IODL source viewer (fileType === "iodl-source"): the SQL + column/row
  // metadata for the clicked IODL citation. Rows are lazy-loaded by qid on the
  // legacy path, or carried inline in ``iodlRows`` for agentic chat (#10389).
  sql: string | null;
  // Which data set the citation is from ("Caissa", "IODL"), so the viewer
  // names its actual source rather than assuming the first one we built.
  sourceLabel: string | null;
  iodlColumns: string[] | null;
  rowCount: number | null;
  iodlReasoning: string | null;
  iodlQid: string | null;
  iodlRows: Record<string, unknown>[] | null;
  /** The artifact this document was opened from, through one of its
   *  citations: the document is a source view, and its header leads with
   *  the way back. Null for every other open, which is what drops it. */
  sourceOf: SourceOrigin | null;
}

const emptyFileViewerState: FileViewerState = {
  navigationEpoch: 0,
  fileId: null,
  fileType: null,
  fileName: null,
  unreadableReason: null,
  isOpen: false,
  doNotOpenDrawer: false,
  sheetName: null,
  highlightPageNum: null,
  highlightContext: null,
  citationRowLabel: null,
  originalNumValue: null,
  parentElementText: null,
  exactOnly: false,
  highlightCell: null,
  previewUrl: null,
  downloadUrl: null,
  artifactId: null,
  interactionId: null,
  interactionError: null,
  sql: null,
  sourceLabel: null,
  iodlColumns: null,
  rowCount: null,
  iodlReasoning: null,
  iodlQid: null,
  iodlRows: null,
  sourceOf: null,
};

export const fileViewerSlice = createSlice({
  name: "fileViewer",
  initialState: emptyFileViewerState,
  reducers: {
    beginFileViewerNavigation: (state) => {
      state.navigationEpoch += 1;
    },
    setFileViewerData: (state, action) => {
      // A payload that names a file identity is an OPEN (or an explicit
      // close passing empty ids): it replaces the whole state, so nothing
      // from the previously-open item survives — an artifact's artifactId
      // leaking into a plain document's header rendered the last artifact's
      // save/version controls on it (Hunter, #11548 feedback 2026-09-01).
      // Identity-less payloads stay merges: highlight refreshes on the open
      // document, isOpen toggles, and version-preview URL swaps on the SAME
      // artifact all patch fields without renaming what is open.
      const payload = action.payload as Partial<FileViewerState>;
      if (
        "fileId" in payload ||
        "interactionId" in payload ||
        "sql" in payload
      ) {
        return {
          ...emptyFileViewerState,
          ...payload,
          navigationEpoch: state.navigationEpoch + 1,
        };
      }
      if (payload.isOpen === false && state.isOpen) state.navigationEpoch += 1;
      Object.assign(state, payload);
    },
    /** Replace the viewer's state rather than merging into it, so nothing from
     *  the previously-open document — a highlight, a sheet name, a citation's
     *  page number — survives into the next one. */
    setFileViewerDataOnlyNew: (state, action) => {
      return {
        ...emptyFileViewerState,
        ...action.payload,
        navigationEpoch: state.navigationEpoch + 1,
      };
    },
    resetFileViewerData: (state) => {
      return {
        ...emptyFileViewerState,
        navigationEpoch: state.navigationEpoch + 1,
      };
    },
  },
});

export const {
  beginFileViewerNavigation,
  setFileViewerData,
  setFileViewerDataOnlyNew,
  resetFileViewerData,
} = fileViewerSlice.actions;
export default fileViewerSlice.reducer;
